const SESSION_KEYS = {
  TOKEN: "auth_token",
  ROLE: "auth_role",
  USER_ID: "auth_userId",
  USER: "auth_user",
};

const ROLE_STORAGE_KEYS = {
  Admin: {
    token: "admin_token",
    role: "admin_role",
    userId: "admin_userId",
    user: "admin_user",
  },
  lead_coordinator: {
    token: "lead_coordinator_token",
    role: "lead_coordinator_role",
    userId: "lead_coordinator_userId",
    user: "lead_coordinator_user",
  },
  hospital: {
    token: "hospital_token",
    role: "hospital_role",
    userId: "hospital_userId",
    user: "hospital_user",
  },
  carepreneur: {
    token: "carepreneur_token",
    role: "carepreneur_role",
    userId: "carepreneur_userId",
    user: "carepreneur_user",
  },
  CareLeader: {
    token: "careleader_token",
    role: "careleader_role",
    userId: "careleader_userId",
    user: "careleader_user",
  },
};

const getRoleStorageKeys = (role) => {
  return ROLE_STORAGE_KEYS[role] || null;
};

export const getAuthToken = () => {
  // Current tab is always the source of truth
  const sessionToken = sessionStorage.getItem(SESSION_KEYS.TOKEN);

  if (sessionToken) {
    return sessionToken;
  }

  // Fresh tab fallback
  const role = sessionStorage.getItem(SESSION_KEYS.ROLE);

  if (role) {
    const keys = getRoleStorageKeys(role);

    if (keys) {
      const token = localStorage.getItem(keys.token);

      if (token) {
        sessionStorage.setItem(SESSION_KEYS.TOKEN, token);
        return token;
      }
    }
  }

  // Legacy fallback
  return localStorage.getItem("token");
};

export const getAuthRole = () => {
  const sessionRole = sessionStorage.getItem(SESSION_KEYS.ROLE);

  if (sessionRole) {
    return sessionRole;
  }

  return localStorage.getItem("role");
};

export const getAuthUserId = () => {
  const sessionUserId = sessionStorage.getItem(SESSION_KEYS.USER_ID);

  if (sessionUserId) {
    return sessionUserId;
  }

  const role = getAuthRole();
  const keys = getRoleStorageKeys(role);

  if (keys) {
    return localStorage.getItem(keys.userId);
  }

  return localStorage.getItem("userId");
};

export const getAuthUser = () => {
  const sessionUser = sessionStorage.getItem(SESSION_KEYS.USER);

  if (sessionUser) {
    try {
      return JSON.parse(sessionUser);
    } catch (e) {
      return null;
    }
  }

  const role = getAuthRole();
  const keys = getRoleStorageKeys(role);

  if (keys && keys.user) {
    const roleUser = localStorage.getItem(keys.user);

    if (roleUser) {
      try {
        sessionStorage.setItem(SESSION_KEYS.USER, roleUser);
        return JSON.parse(roleUser);
      } catch (e) {
        return null;
      }
    }
  }

  const legacyUser = localStorage.getItem("user");
  if (legacyUser) {
    try {
      return JSON.parse(legacyUser);
    } catch (e) {
      return null;
    }
  }

  return null;
};

export const loginWithToken = ({ token, role, userId, user }) => {
  if (!token || !role) {
    return;
  }

  // Current tab session
  sessionStorage.setItem(SESSION_KEYS.TOKEN, token);
  sessionStorage.setItem(SESSION_KEYS.ROLE, role);

  if (userId !== undefined && userId !== null) {
    sessionStorage.setItem(SESSION_KEYS.USER_ID, String(userId));
  }

  if (user !== undefined && user !== null) {
    const userStr = typeof user === "string" ? user : JSON.stringify(user);
    sessionStorage.setItem(SESSION_KEYS.USER, userStr);
  }

  // Persistent role-specific credentials
  const keys = getRoleStorageKeys(role);

  if (keys) {
    localStorage.setItem(keys.token, token);
    localStorage.setItem(keys.role, role);

    if (userId !== undefined && userId !== null) {
      localStorage.setItem(keys.userId, String(userId));
    }

    if (user !== undefined && user !== null && keys.user) {
      const userStr = typeof user === "string" ? user : JSON.stringify(user);
      localStorage.setItem(keys.user, userStr);
    }
  }

  // Legacy compatibility
  // Keep these only if old parts of the application still need them.
  localStorage.setItem("token", token);
  localStorage.setItem("role", role);

  if (userId !== undefined && userId !== null) {
    localStorage.setItem("userId", String(userId));
  }

  if (user !== undefined && user !== null) {
    const userStr = typeof user === "string" ? user : JSON.stringify(user);
    localStorage.setItem("user", userStr);
  }
};

export const initTabSession = () => {
  // Already logged in in this tab
  if (sessionStorage.getItem(SESSION_KEYS.TOKEN)) {
    return;
  }

  const role = sessionStorage.getItem(SESSION_KEYS.ROLE);

  if (!role) {
    return;
  }

  const keys = getRoleStorageKeys(role);

  if (!keys) {
    return;
  }

  const token = localStorage.getItem(keys.token);
  const userId = localStorage.getItem(keys.userId);
  const user = keys.user ? localStorage.getItem(keys.user) : null;

  if (token) {
    sessionStorage.setItem(SESSION_KEYS.TOKEN, token);
    sessionStorage.setItem(SESSION_KEYS.ROLE, role);

    if (userId) {
      sessionStorage.setItem(SESSION_KEYS.USER_ID, userId);
    }

    if (user) {
      sessionStorage.setItem(SESSION_KEYS.USER, user);
    }
  }
};

export const logout = () => {
  const role = getAuthRole();
  const keys = getRoleStorageKeys(role);

  // Clear ONLY current tab session
  sessionStorage.removeItem(SESSION_KEYS.TOKEN);
  sessionStorage.removeItem(SESSION_KEYS.ROLE);
  sessionStorage.removeItem(SESSION_KEYS.USER_ID);
  sessionStorage.removeItem(SESSION_KEYS.USER);

  // Clear ONLY this role's persistent credentials
  if (keys) {
    localStorage.removeItem(keys.token);
    localStorage.removeItem(keys.role);
    localStorage.removeItem(keys.userId);
    if (keys.user) {
      localStorage.removeItem(keys.user);
    }
  }

  // Do NOT clear the opposite role.
};